import type { AdminInspectorDetailDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { observer } from 'mobx-react-lite';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ApproveKycDialog } from './_components/ApproveKycDialog';
import { CardsTab } from './_components/CardsTab';
import { DeactivateInspectorDialog } from './_components/DeactivateInspectorDialog';
import { InspectorDetailFooter } from './_components/InspectorDetailFooter';
import { InspectorDetailHeader } from './_components/InspectorDetailHeader';
import { KycTab } from './_components/KycTab';
import { OrdersTab } from './_components/OrdersTab';
import { PricingTab } from './_components/PricingTab';
import { ProfileTab } from './_components/ProfileTab';
import { ReactivateInspectorDialog } from './_components/ReactivateInspectorDialog';
import { canApproveKyc } from './_lib/canApproveKyc';
import { canReinstate, canSuspend } from './_lib/inspectorStatusActions';
import { useApproveKyc } from './_lib/useApproveKyc';
import { useInspectorStatusActions } from './_lib/useInspectorStatusActions';
import { useAdminInspectorDetail } from '../../api/admin-inspectors';

/**
 * Inspector detail drawer (AUT-125 / ADMIN-13).
 *
 * Right-side 720px drawer with the 5-tab nav (KYC / Профиль / Заказы /
 * Цены / Карты) per mockup + ticket Output files. NO 6th "Журнал" tab —
 * out of scope.
 *
 * The Cards tab (`CardsTab`, AUT-126) fetches the inspector's bound payout
 * cards (`GET /admin/cards?inspectorId=<id>`) and reuses the SAME shared
 * `<CardsGrid>` renderer + force-unbind step-up as the standalone `/cards`
 * page — the query is gated to the active tab via the `tab === 'cards'`
 * mount guard below, so it never fires until the operator opens the tab.
 *
 * Footer wires three actions, each gated by the substep-2 helpers:
 *   - 'Одобрить KYC' — `canApproveKyc(kyc)` (🟠 #1, session-based)
 *   - 'Деактивировать' — `canSuspend(status)` (🟠 #2, FSM-based)
 *   - 'Восстановить' — `canReinstate(status)` (🟠 #2, FSM-based)
 *
 * Each action is role + permission + audit gated server-side (AUT-138
 * removed per-action TOTP step-up). KYC approve uses `useApproveKyc` which
 * patches the detail cache from the response (architect 🟠 #5) so the
 * footer doesn't flicker the button while the refetch is in flight. Status
 * PATCH uses `useInspectorStatusActions` which only invalidates the prefix
 * — the dto envelope is list-shape.
 *
 * Opens when `inspectorId` is non-null; the detail query is `enabled`
 * only then, so closing it stops the fetch.
 */
const DRAWER_WIDTH = 720;

type TabKey = 'kyc' | 'profile' | 'orders' | 'pricing' | 'cards';

interface InspectorDetailDrawerProps {
  inspectorId: string | null;
  onClose: () => void;
}

/** Tab nav + tab body (extracted to keep `DrawerBody` under the 80-line cap). */
const DrawerTabs = ({
  inspector,
  tab,
  onTabChange,
}: {
  inspector: AdminInspectorDetailDto;
  tab: TabKey;
  onTabChange: (next: TabKey) => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <Tabs
        value={tab}
        onChange={(_e, v: TabKey) => onTabChange(v)}
        sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider', minHeight: 44 }}
      >
        <Tab value="kyc" label={t('inspectors.detail.tabs.kyc')} />
        <Tab value="profile" label={t('inspectors.detail.tabs.profile')} />
        <Tab
          value="orders"
          label={`${t('inspectors.detail.tabs.orders')} (${inspector.ordersSummary.total})`}
        />
        <Tab value="pricing" label={t('inspectors.detail.tabs.pricing')} />
        <Tab value="cards" label={t('inspectors.detail.tabs.cards')} />
      </Tabs>
      <Box sx={{ flex: 1, overflow: 'auto', p: '22px 24px' }}>
        {tab === 'kyc' ? <KycTab inspector={inspector} /> : null}
        {tab === 'profile' ? <ProfileTab inspector={inspector} /> : null}
        {tab === 'orders' ? <OrdersTab inspector={inspector} /> : null}
        {tab === 'pricing' ? <PricingTab inspector={inspector} /> : null}
        {tab === 'cards' ? <CardsTab inspector={inspector} /> : null}
      </Box>
    </>
  );
};

const DrawerBody = observer(
  ({
    inspector,
    onClose,
  }: {
    inspector: AdminInspectorDetailDto;
    onClose: () => void;
  }): ReactElement => {
    const [tab, setTab] = useState<TabKey>('kyc');
    const [approveOpen, setApproveOpen] = useState(false);
    const [deactivateOpen, setDeactivateOpen] = useState(false);
    const [reactivateOpen, setReactivateOpen] = useState(false);

    const approveKyc = useApproveKyc();
    const statusActions = useInspectorStatusActions();
    const busy = approveKyc.busy || statusActions.busy;

    const onConfirmApprove = (): void => {
      void approveKyc.run(inspector.id).then((ok) => {
        if (ok) setApproveOpen(false);
      });
    };

    const onConfirmStatus = (next: 'suspended' | 'active', reason: string): void => {
      void statusActions.run(inspector.id, next, reason).then((ok) => {
        if (ok) {
          setDeactivateOpen(false);
          setReactivateOpen(false);
        }
      });
    };

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            maxWidth: '100vw',
          }}
        >
          <InspectorDetailHeader inspector={inspector} onClose={onClose} />
          <DrawerTabs inspector={inspector} tab={tab} onTabChange={setTab} />
          <InspectorDetailFooter
            canApproveKyc={canApproveKyc(inspector.kyc)}
            canSuspend={canSuspend(inspector.status)}
            canReinstate={canReinstate(inspector.status)}
            busy={busy}
            onApproveKyc={() => setApproveOpen(true)}
            onSuspend={() => setDeactivateOpen(true)}
            onReactivate={() => setReactivateOpen(true)}
          />
        </Box>

        <ApproveKycDialog
          open={approveOpen}
          loading={busy}
          onConfirm={onConfirmApprove}
          onCancel={() => setApproveOpen(false)}
        />
        <DeactivateInspectorDialog
          open={deactivateOpen}
          activeOrdersCount={inspector.ordersSummary.active}
          loading={busy}
          onConfirm={(reason) => onConfirmStatus('suspended', reason)}
          onCancel={() => setDeactivateOpen(false)}
        />
        <ReactivateInspectorDialog
          open={reactivateOpen}
          loading={busy}
          onConfirm={(reason) => onConfirmStatus('active', reason)}
          onCancel={() => setReactivateOpen(false)}
        />
      </>
    );
  },
);

export const InspectorDetailDrawer = ({
  inspectorId,
  onClose,
}: InspectorDetailDrawerProps): ReactElement => {
  const { t } = useTranslation();
  const query = useAdminInspectorDetail(inspectorId);

  return (
    <Drawer anchor="right" open={inspectorId !== null} onClose={onClose}>
      {query.isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      {query.error ? (
        <Box sx={{ width: { xs: '100vw', sm: DRAWER_WIDTH }, p: 3 }}>
          <Alert severity="error">{t('inspectors.detail.loadError')}</Alert>
        </Box>
      ) : null}
      {query.data ? (
        <DrawerBody key={query.data.id} inspector={query.data} onClose={onClose} />
      ) : null}
    </Drawer>
  );
};
